<script setup>
/* global axios */
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useFunctionGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { accountScopedRoute, accountId } = useAccount();
const { t } = useI18n();

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'agora_bi'
);

onMounted(() => {
  store.dispatch('integrations/get');
});

const baseUrl = computed(() => {
  const hooks = integration.value?.hooks || [];
  const raw = hooks[0]?.settings?.url || '';
  return raw.replace(/\/$/, '');
});

/**
 * API expects a relative path (e.g. /collection/5-agora). Admins often paste the full BI URL;
 * strip origin when it matches the configured base URL.
 */
function normalizeBiReturnTo(raw, base) {
  const s = (raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const b = new URL(base.match(/^https?:\/\//i) ? base : `https://${base}`);
      if (u.hostname !== b.hostname) return '';
      const path = `${u.pathname}${u.search}`;
      return path || '/';
    } catch {
      return '';
    }
  }
  return s.startsWith('/') ? s : `/${s}`;
}

// Optional: collection path (or full URL on same host as Base URL)
const collectionPathRaw = computed(() => {
  const hooks = integration.value?.hooks || [];
  return hooks[0]?.settings?.collection_path?.trim() || '';
});

const collectionPath = computed(() =>
  normalizeBiReturnTo(collectionPathRaw.value, baseUrl.value)
);

const isConfigured = computed(() => !!baseUrl.value);

// ── Collection-embed mode ────────────────────────────────────────────────────

const collectionIframeSrc = ref('');
const collectionLoading = ref(false);
const collectionLoadError = ref(false);

async function fetchCollectionUrl(path) {
  collectionLoading.value = true;
  collectionLoadError.value = false;
  collectionIframeSrc.value = '';
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/integrations/agora_bi/collection_url`,
      {
        params: { return_to: path },
      }
    );
    collectionIframeSrc.value = data.embed_url;
  } catch {
    collectionLoadError.value = true;
  } finally {
    collectionLoading.value = false;
  }
}

// ── Reactive loading ─────────────────────────────────────────────────────────

const defaultPath = computed(() => {
  const normalized = collectionPath.value;
  if (normalized) return normalized;
  if (!collectionPathRaw.value) return '/collection/root';
  return '';
});

watch(
  [baseUrl, collectionPathRaw, defaultPath],
  ([base, , path]) => {
    if (!base) return;
    if (!path) {
      collectionLoading.value = false;
      collectionLoadError.value = false;
      collectionIframeSrc.value = '';
      return;
    }
    fetchCollectionUrl(path);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 h-full w-full bg-n-background">
    <!-- Not configured -->
    <div
      v-if="!isConfigured"
      class="flex flex-col items-center justify-center flex-1 gap-4 px-4 text-center text-n-slate-11"
    >
      <p>{{ t('APPS_BI_PAGE.NOT_CONFIGURED') }}</p>
      <router-link
        :to="accountScopedRoute('settings_applications')"
        class="text-n-brand font-medium hover:underline"
      >
        {{ t('APPS_BI_PAGE.CONFIGURE_LINK') }}
      </router-link>
    </div>

    <template v-else>
      <!-- Loading -->
      <div
        v-if="collectionLoading"
        class="flex flex-col items-center justify-center flex-1 gap-2 text-n-slate-11"
      >
        <span class="i-lucide-loader-circle animate-spin text-2xl" />
        <p>{{ t('APPS_BI_PAGE.LOADING_COLLECTION') }}</p>
      </div>

      <!-- Invalid path (e.g. full URL pointing at another host) -->
      <div
        v-else-if="collectionPathRaw && !collectionPath"
        class="flex flex-col items-center justify-center flex-1 gap-4 px-4 text-center text-n-slate-11"
      >
        <span class="i-lucide-circle-alert text-3xl text-n-slate-11" />
        <p>{{ t('APPS_BI_PAGE.COLLECTION_PATH_INVALID') }}</p>
      </div>

      <!-- Load error -->
      <div
        v-else-if="collectionLoadError"
        class="flex flex-col items-center justify-center flex-1 gap-4 px-4 text-center text-n-slate-11"
      >
        <span class="i-lucide-circle-x text-3xl text-n-red-9" />
        <p>{{ t('APPS_BI_PAGE.COLLECTION_LOAD_ERROR') }}</p>
      </div>

      <!-- Full-page collection iframe -->
      <iframe
        v-else-if="collectionIframeSrc"
        :key="collectionIframeSrc"
        title="Agora BI"
        :src="collectionIframeSrc"
        class="w-full h-full min-h-0 border-0 flex-1"
        allowfullscreen
      />
    </template>
  </div>
</template>
